import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="command-palette"
export default class extends Controller {
  static targets = ["input", "results", "noResults"]
  static values = {
    commands: Array
  }

  connect() {
    this.activeIndex = -1
    // Listen for Cmd+K or Ctrl+K
    this.boundHandleKeypress = this.handleKeypress.bind(this)
    document.addEventListener("keydown", this.boundHandleKeypress)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeypress)
  }

  handleKeypress(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
    }
  }

  open() {
    // Dispatch event to open modal
    const modal = document.querySelector('[data-controller~="modal"][data-modal-type="command-palette"]')
    if (modal) {
      const controller = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (controller) {
        controller.open()
        // Show everything immediately — an empty panel until the first
        // keystroke reads as broken.
        this.inputTarget.value = ""
        this.showAllCommands()
        requestAnimationFrame(() => this.inputTarget.focus())
      }
    }
  }

  search(event) {
    const query = event.target.value.toLowerCase().trim()

    if (!query) {
      this.showAllCommands()
      return
    }

    const results = this.commandsValue.filter(cmd =>
      cmd.name.toLowerCase().includes(query) ||
      cmd.keywords?.some(k => k.toLowerCase().includes(query))
    )

    this.displayResults(results)
  }

  showAllCommands() {
    this.displayResults(this.commandsValue)
  }

  displayResults(results) {
    this.activeIndex = -1

    if (results.length === 0) {
      this.resultsTarget.classList.add("hidden")
      this.noResultsTarget.classList.remove("hidden")
      return
    }

    this.noResultsTarget.classList.add("hidden")
    this.resultsTarget.classList.remove("hidden")

    this.resultsTarget.innerHTML = results.map(cmd => `
      <a href="${cmd.url}"
         data-turbo-frame="${cmd.turboFrame || '_top'}"
         class="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors duration-150 cursor-pointer group">
        <div class="flex items-center justify-center w-10 h-10 rounded-lg ${cmd.iconBg || 'bg-indigo-100'} group-hover:scale-110 transition-transform duration-150">
          ${cmd.icon}
        </div>
        <div class="flex-1">
          <div class="font-medium text-gray-900">${cmd.name}</div>
          ${cmd.description ? `<div class="text-xs text-gray-500">${cmd.description}</div>` : ''}
        </div>
        ${cmd.badge ? `<span class="px-2 py-1 text-xs font-medium rounded-full ${cmd.badgeClass || 'bg-gray-100 text-gray-600'}">${cmd.badge}</span>` : ''}
      </a>
    `).join('')
  }

  // Keyboard interaction on the search input: the footer promises
  // ArrowUp/ArrowDown + Enter, so all three are handled here.
  selectResult(event) {
    const links = Array.from(this.resultsTarget.querySelectorAll("a"))
    if (links.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.setActive(links, (this.activeIndex + 1) % links.length)
        break
      case "ArrowUp":
        event.preventDefault()
        this.setActive(links, (this.activeIndex - 1 + links.length) % links.length)
        break
      case "Enter": {
        event.preventDefault()
        const target = links[this.activeIndex] || links[0]
        target.click()
        break
      }
    }
  }

  setActive(links, index) {
    if (this.activeIndex >= 0 && links[this.activeIndex]) {
      links[this.activeIndex].classList.remove("bg-indigo-50")
      links[this.activeIndex].removeAttribute("aria-selected")
    }
    this.activeIndex = index
    const link = links[index]
    link.classList.add("bg-indigo-50")
    link.setAttribute("aria-selected", "true")
    link.scrollIntoView({ block: "nearest" })
  }
}
